import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logActivity } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  Field,
  Input,
  Textarea,
} from "@/pages/admin/components/ui";

type SectionData = Record<string, Record<string, unknown>>;

const TABS = [
  { key: "hero", label: "Hero" },
  { key: "servicios", label: "Servicios" },
  { key: "proyectos", label: "Proyectos" },
  { key: "tecnologias", label: "Tecnologías" },
  { key: "about", label: "Sobre nosotros" },
  { key: "proceso", label: "Proceso" },
  { key: "por-que", label: "Por qué nosotros" },
  { key: "contacto", label: "Contacto" },
  { key: "footer", label: "Footer" },
];

const DEFAULTS: SectionData = {
  hero: { badge: "", title: "", subtitle: "", btn_primary: "", btn_secondary: "" },
  servicios: { title: "", subtitle: "" },
  proyectos: { title: "", subtitle: "" },
  tecnologias: { title: "", subtitle: "" },
  about: { title: "", subtitle: "", mision: "", vision: "" },
  proceso: { title: "", steps: [] },
  "por-que": { title: "", features: [] },
  contacto: { title: "", description: "", btn_text: "" },
  footer: { description: "", copyright: "", tagline: "" },
};

interface Step {
  numero: string;
  titulo: string;
  descripcion: string;
}
interface Feature {
  titulo: string;
  descripcion: string;
  icono: string;
}

export default function Contenido() {
  const { success, error: toastError } = useToast();
  const [data, setData] = useState<SectionData>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("hero");

  const load = useCallback(async () => {
    const { data: rows } = await supabase.from("content_sections").select("seccion, valor");
    const map: SectionData = { ...DEFAULTS };
    (rows ?? []).forEach((r) => {
      map[r.seccion] = { ...(map[r.seccion] || {}), ...((r.valor as Record<string, unknown>) || {}) };
    });
    setData(map);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function setValue(seccion: string, key: string, value: unknown) {
    setData((d) => ({ ...d, [seccion]: { ...d[seccion], [key]: value } }));
  }

  function setSteps(steps: Step[]) {
    setData((d) => ({ ...d, proceso: { ...d.proceso, steps } }));
  }
  function setFeatures(features: Feature[]) {
    setData((d) => ({ ...d, "por-que": { ...d["por-que"], features } }));
  }

  async function saveSection(seccion: string, clave: string, valor: unknown) {
    const { data: existing } = await supabase
      .from("content_sections")
      .select("id")
      .eq("seccion", seccion)
      .eq("clave", clave)
      .maybeSingle();
    if (existing) {
      await supabase.from("content_sections").update({ valor }).eq("id", existing.id);
    } else {
      await supabase.from("content_sections").insert({ seccion, clave, valor });
    }
  }

  async function save() {
    setSaving(true);
    try {
      for (const tab of TABS) {
        await saveSection(tab.key, tab.key, data[tab.key] || {});
      }
      await logActivity("actualizó el contenido del sitio");
      success("Cambios guardados correctamente.");
    } catch {
      toastError("No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  }

  function preview() {
    window.open(`${__BASE_PATH__ || ""}/`, "_blank");
  }

  if (loading) return <Spinner label="Cargando contenido…" />;

  const hero = data.hero || {};
  const serv = data.servicios || {};
  const proy = data.proyectos || {};
  const tech = data.tecnologias || {};
  const about = data.about || {};
  const proceso = data.proceso || {};
  const porQue = data["por-que"] || {};
  const contacto = data.contacto || {};
  const footer = data.footer || {};

  return (
    <div>
      <PageHeader
        title="Contenido del sitio"
        subtitle="Edita los textos visibles en la página pública."
        actions={
          <>
            <Button variant="secondary" onClick={preview}>
              <i className="ri-external-link-line" />
              Vista previa
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-1 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              active === tab.key
                ? "bg-primary-500 text-white"
                : "bg-white border border-background-200 text-foreground-600 hover:border-primary-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Card className="p-6">
        {active === "hero" && (
          <div className="space-y-4">
            <Field label="Badge">
              <Input value={String(hero.badge || "")} onChange={(e) => setValue("hero", "badge", e.target.value)} />
            </Field>
            <Field label="Título principal">
              <Input value={String(hero.title || "")} onChange={(e) => setValue("hero", "title", e.target.value)} />
            </Field>
            <Field label="Subtítulo">
              <Textarea rows={3} value={String(hero.subtitle || "")} onChange={(e) => setValue("hero", "subtitle", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Botón principal">
                <Input value={String(hero.btn_primary || "")} onChange={(e) => setValue("hero", "btn_primary", e.target.value)} />
              </Field>
              <Field label="Botón secundario">
                <Input value={String(hero.btn_secondary || "")} onChange={(e) => setValue("hero", "btn_secondary", e.target.value)} />
              </Field>
            </div>
          </div>
        )}

        {active === "servicios" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(serv.title || "")} onChange={(e) => setValue("servicios", "title", e.target.value)} /></Field>
            <Field label="Subtítulo"><Textarea rows={3} value={String(serv.subtitle || "")} onChange={(e) => setValue("servicios", "subtitle", e.target.value)} /></Field>
          </div>
        )}

        {active === "proyectos" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(proy.title || "")} onChange={(e) => setValue("proyectos", "title", e.target.value)} /></Field>
            <Field label="Subtítulo"><Textarea rows={3} value={String(proy.subtitle || "")} onChange={(e) => setValue("proyectos", "subtitle", e.target.value)} /></Field>
          </div>
        )}

        {active === "tecnologias" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(tech.title || "")} onChange={(e) => setValue("tecnologias", "title", e.target.value)} /></Field>
            <Field label="Subtítulo"><Textarea rows={3} value={String(tech.subtitle || "")} onChange={(e) => setValue("tecnologias", "subtitle", e.target.value)} /></Field>
          </div>
        )}

        {active === "about" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(about.title || "")} onChange={(e) => setValue("about", "title", e.target.value)} /></Field>
            <Field label="Descripción"><Textarea rows={3} value={String(about.subtitle || "")} onChange={(e) => setValue("about", "subtitle", e.target.value)} /></Field>
            <Field label="Misión"><Textarea rows={2} value={String(about.mision || "")} onChange={(e) => setValue("about", "mision", e.target.value)} /></Field>
            <Field label="Visión"><Textarea rows={2} value={String(about.vision || "")} onChange={(e) => setValue("about", "vision", e.target.value)} /></Field>
          </div>
        )}

        {active === "proceso" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(proceso.title || "")} onChange={(e) => setValue("proceso", "title", e.target.value)} /></Field>
            <div className="space-y-3">
              {(proceso.steps as Step[])?.map((step, i) => (
                <div key={i} className="grid grid-cols-[60px_1fr_1fr_auto] gap-2 items-start">
                  <Input value={step.numero} onChange={(e) => setSteps((proceso.steps as Step[]).map((s, j) => (j === i ? { ...s, numero: e.target.value } : s)))} />
                  <Input value={step.titulo} onChange={(e) => setSteps((proceso.steps as Step[]).map((s, j) => (j === i ? { ...s, titulo: e.target.value } : s)))} />
                  <Input value={step.descripcion} onChange={(e) => setSteps((proceso.steps as Step[]).map((s, j) => (j === i ? { ...s, descripcion: e.target.value } : s)))} />
                  <button onClick={() => setSteps((proceso.steps as Step[]).filter((_, j) => j !== i))} className="w-9 h-9 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line" /></button>
                </div>
              ))}
              <Button variant="secondary" onClick={() => setSteps([...(proceso.steps as Step[]) || [], { numero: String(((proceso.steps as Step[])?.length || 0) + 1).padStart(2, "0"), titulo: "", descripcion: "" }])}>
                <i className="ri-add-line" /> Añadir paso
              </Button>
            </div>
          </div>
        )}

        {active === "por-que" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(porQue.title || "")} onChange={(e) => setValue("por-que", "title", e.target.value)} /></Field>
            <div className="space-y-3">
              {(porQue.features as Feature[])?.map((f, i) => (
                <div key={i} className="grid grid-cols-[1fr_2fr_1fr_auto] gap-2 items-start">
                  <Input value={f.icono} onChange={(e) => setFeatures((porQue.features as Feature[]).map((x, j) => (j === i ? { ...x, icono: e.target.value } : x)))} placeholder="icono" />
                  <Input value={f.titulo} onChange={(e) => setFeatures((porQue.features as Feature[]).map((x, j) => (j === i ? { ...x, titulo: e.target.value } : x)))} />
                  <Input value={f.descripcion} onChange={(e) => setFeatures((porQue.features as Feature[]).map((x, j) => (j === i ? { ...x, descripcion: e.target.value } : x)))} />
                  <button onClick={() => setFeatures((porQue.features as Feature[]).filter((_, j) => j !== i))} className="w-9 h-9 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line" /></button>
                </div>
              ))}
              <Button variant="secondary" onClick={() => setFeatures([...(porQue.features as Feature[]) || [], { icono: "ri-star-line", titulo: "", descripcion: "" }])}>
                <i className="ri-add-line" /> Añadir característica
              </Button>
            </div>
          </div>
        )}

        {active === "contacto" && (
          <div className="space-y-4">
            <Field label="Título"><Input value={String(contacto.title || "")} onChange={(e) => setValue("contacto", "title", e.target.value)} /></Field>
            <Field label="Descripción"><Textarea rows={3} value={String(contacto.description || "")} onChange={(e) => setValue("contacto", "description", e.target.value)} /></Field>
            <Field label="Texto del botón"><Input value={String(contacto.btn_text || "")} onChange={(e) => setValue("contacto", "btn_text", e.target.value)} /></Field>
          </div>
        )}

        {active === "footer" && (
          <div className="space-y-4">
            <Field label="Descripción"><Textarea rows={2} value={String(footer.description || "")} onChange={(e) => setValue("footer", "description", e.target.value)} /></Field>
            <Field label="Copyright"><Input value={String(footer.copyright || "")} onChange={(e) => setValue("footer", "copyright", e.target.value)} /></Field>
            <Field label="Eslogan"><Input value={String(footer.tagline || "")} onChange={(e) => setValue("footer", "tagline", e.target.value)} /></Field>
          </div>
        )}
      </Card>
    </div>
  );
}